/* ============================================================
   Data client — one hook per contract source, four states out of the box.
   `useContract('site')` fetches /api/site and returns an envelope plus a
   WidgetBody-ready `state` (loading / empty / error / live) and a live-pulse
   `syncedAt`. If the endpoint can't be reached (no functions running yet, or a
   transient failure) it degrades to the bundled placeholder rather than
   blocking the surface — the FOUNDER RULE. Polling drives "updated Ns ago".
   ============================================================ */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Contract, ContractKey } from './contract';
import { PLACEHOLDER } from './placeholder';
import type { SourceState } from './flags';

export interface Envelope<T> {
  source: SourceState;
  syncedAt: string; // ISO timestamp the server (or fallback) produced this
  data: T;
}

export type WidgetState = 'live' | 'loading' | 'empty' | 'error';

export interface ContractQuery<K extends ContractKey> {
  envelope: Envelope<Contract[K]> | undefined;
  data: Contract[K] | undefined;
  source: SourceState | undefined;
  syncedAt: string | undefined;
  state: WidgetState;
  isPlaceholder: boolean;
  refetch: () => void;
  query: UseQueryResult<Envelope<Contract[K]>>;
}

/** True when a slice has no rows worth showing (drives the `empty` state). */
function isEmpty(key: ContractKey, data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  // Collection-shaped sources: empty when their primary list is empty.
  const d = data as Record<string, unknown>;
  if (key === 'reviews') return Array.isArray(d.feed) && d.feed.length === 0;
  if (key === 'code') return Array.isArray(d.prs) && d.prs.length === 0 && Array.isArray(d.deploys) && (d.deploys as unknown[]).length === 0;
  if (key === 'kanban') return Object.values(d).every((v) => Array.isArray(v) && v.length === 0);
  if (key === 'threads') return Object.keys(d).length === 0;
  // heartbeat never reads "empty" — a never/offline row is meaningful (renders
  // "not connected"/OFFLINE), so it always shows rather than an empty state.
  return false;
}

async function fetchSource<K extends ContractKey>(key: K): Promise<Envelope<Contract[K]>> {
  const res = await fetch(`/api/${key}`, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`/api/${key} → ${res.status}`);
  const json = (await res.json()) as Envelope<Contract[K]>;
  if (!json || typeof json !== 'object' || !('data' in json)) {
    throw new Error(`/api/${key} → malformed envelope`);
  }
  return json;
}

export interface UseContractOptions {
  /** Poll interval in ms for the live pulse. 0 disables polling. */
  pollMs?: number;
  /** When false, fall through to the `error` state instead of placeholder. */
  fallbackToPlaceholder?: boolean;
  enabled?: boolean;
}

export function useContract<K extends ContractKey>(
  key: K,
  opts: UseContractOptions = {},
): ContractQuery<K> {
  const { pollMs = 30_000, fallbackToPlaceholder = true, enabled = true } = opts;

  const query = useQuery<Envelope<Contract[K]>>({
    queryKey: ['contract', key],
    queryFn: () => fetchSource(key),
    enabled,
    refetchInterval: pollMs > 0 ? pollMs : false,
    staleTime: pollMs > 0 ? pollMs / 2 : 15_000,
    retry: 1,
  });

  let envelope = query.data;
  let usedFallback = false;
  if (!envelope && query.isError && fallbackToPlaceholder) {
    // Never block the surface — render the bundled placeholder, flagged as such.
    envelope = { source: 'placeholder', syncedAt: new Date().toISOString(), data: PLACEHOLDER[key] };
    usedFallback = true;
  }

  let state: WidgetState;
  if (query.isLoading && !envelope) state = 'loading';
  else if (query.isError && !envelope) state = 'error';
  else if (envelope && isEmpty(key, envelope.data)) state = 'empty';
  else state = 'live';

  return {
    envelope,
    data: envelope?.data,
    source: envelope?.source,
    syncedAt: envelope?.syncedAt,
    state,
    isPlaceholder: (envelope?.source ?? (usedFallback ? 'placeholder' : undefined)) === 'placeholder',
    refetch: () => void query.refetch(),
    query,
  };
}
